import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {Subject} from 'rxjs';
import {take, takeUntil} from 'rxjs/operators';
import {Sort} from '@angular/material/sort';
import {MatTableDataSource} from '@angular/material/table';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {BenefitDTO} from '../../../../models/BenefitDTO';
import {BenefitService} from '../../../../services/benefit.service';
import {SharedService} from '../../../../../common/services/shared-service';
import {getSortDirection} from '../../../../../common/util/Util';
import {DateUtil} from '../../../../../common/util/DateUtil';
import {TableControl} from '../../../../../common/models/TableControl';
import {EntRequestBuilder} from '../../../../services/ent-request-builder';
import {PagedResponseDTO} from '../../../../../common/models/PagedResponseDTO';
import {ConfirmationDialogComponent} from '../../../../../common/dialogs/confirmation-dialog/confirmation-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {AddBenefitDialogComponent} from '../../../dialogs/add-benefit-dialog/add-benefit-dialog.component';
import {detailsExpand} from '../../../../../common';

@Component({
  selector: 'app-benefits',
  templateUrl: './benefits.component.html',
  styleUrls: [
    '../../../../../../../styles/global/_management-styles.scss',
    '../../../../../../../styles/global/_add-view-edit-styles.scss',
  ],
  animations: [detailsExpand]
})
export class BenefitsComponent implements OnInit, OnDestroy {
  readonly destroyed$ = new Subject();
  readonly dateFormat = DateUtil.dateFormat;
  readonly dateTimeFormat = DateUtil.dateTimeFormat;

  benefitsDataSource: MatTableDataSource<BenefitDTO> = new MatTableDataSource();
  benefitsDisplayedColumns: string[] = [
    'icons',
    'benefitId',
    'name',
    'benefitCode',
    'ageMin',
    'ageMax'
  ];
  benefitsExpandedRow: BenefitDTO | null;
  benefitsTableControl: TableControl;

  // MatPaginator
  @ViewChild('benefitsPaginator', {static: false}) benefitsPaginator: MatPaginator;

  constructor(public dialog: MatDialog,
              private benefitService: BenefitService,
              private route: ActivatedRoute,
              private sharedService: SharedService) {
    const controls = EntRequestBuilder.getTableControls();
    this.benefitsTableControl = new TableControl(
      controls.length,
      controls.pageSize,
      controls.pageNumber,
      controls.pageSizeOptions,
      'benefitId',
      'DESCENDING',
      'name',
      ''
    );

  }

  ngOnInit(): void {
    this.getListOfBenefits(this.benefitsTableControl);
    this.initSubscriptions();
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  initSubscriptions() {
    this.route
      .queryParams
      .subscribe(params => {
        if (params.dialog === 'true') {
          this.openBenefitDialog();
        }
      });
  }

  pageChanged($event: PageEvent) {
    this.benefitsTableControl.pageNumber = $event.pageIndex;
    this.benefitsTableControl.pageSize = $event.pageSize;
    this.getListOfBenefits(this.benefitsTableControl);
  }

  sortData($event: Sort) {
    if (!$event.direction) {
      return;
    }
    if (this.benefitsPaginator != null) {
      this.benefitsPaginator.firstPage();
    }
    this.benefitsTableControl.pageNumber = 0;
    this.benefitsTableControl.sortField = $event.active;
    this.benefitsTableControl.sortDirection = getSortDirection($event.direction);
    this.getListOfBenefits(this.benefitsTableControl);
  }

  onValueChanged(value: string) {
    this.benefitsTableControl.filterField = 'name';
    this.benefitsTableControl.filterValue = value;
    this.getListOfBenefits(this.benefitsTableControl);
  }

  onDeleteBenefitConfirmation(event: MouseEvent, benefit: BenefitDTO) {
    event.preventDefault();
    event.stopPropagation();

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '40vw',
      data: `Are you sure you wish to delete this ${benefit.name} benefit?`
    });

    dialogRef
      .afterClosed()
      .subscribe((isConfirmed) => {
        if (isConfirmed) {
          this.onDeleteBenefitConfirmed(benefit.benefitId);
        }
      });
  }

  onDeleteBenefitConfirmed(benefitId: bigint) {
    this.benefitService.deleteBenefit(benefitId)
      .pipe(
        take(1)
      )
      .subscribe(
        (response) => {
          this.sharedService.openSnackBar(`Benefit with ${benefitId} deleted`, 'Dismiss');
          this.getListOfBenefits(this.benefitsTableControl);
        },
        (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      );
  }

  getListOfBenefits(tableControl: TableControl): void {
    const pagedRequestDTO = EntRequestBuilder.getListOfBenefitsPagedRequestDTO(tableControl);
    this.benefitService
      .getListOfBenefits(pagedRequestDTO)
      .pipe(
        takeUntil(this.destroyed$)
      )
      .subscribe(
        (response: PagedResponseDTO<BenefitDTO>) => {
          this.benefitsDataSource.data = response.resultList;
          this.benefitsTableControl.length = response.totalRecords;
        },
        (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      );
  }

  editEntry(event: MouseEvent, benefitId: bigint) {
    event.preventDefault();
    event.stopPropagation();
    this.openBenefitDialog(benefitId);
  }

  openBenefitDialog(benefitId?: bigint) {
    const dialogRef = this.dialog.open(AddBenefitDialogComponent, {
      width: '50vw',
      data: {
        benefitId: benefitId
      },
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(
      (result) => {
        if (result.isSaved) {
          this.getListOfBenefits(this.benefitsTableControl);
        }
      },
      (error) => {
        this.sharedService.openErrorDialog(error.message);
      }
    );
  }

}
